import { useCallback, useEffect, useRef, useState } from "react";
import { preferOpusOnly } from "../lib/opus";
import type { NetworkStats, SignalEnvelope } from "../types";

const EMPTY_STATS: NetworkStats = {
  rtt: 0,
  jitter: 0,
  packetLoss: 0,
  bitrate: 0,
  candidate: "—",
  mode: "unknown",
  quality: "unknown",
};

type CandidateStats = {
  candidateType?: string;
  protocol?: string;
};

type Options = {
  iceServers: RTCIceServer[];
  sendSignal: (message: SignalEnvelope) => void;
  callId: string | null;
  onConnected: () => void;
  onRecovering: () => void;
  onFailed: () => void;
};

export function useWebRTC(options: Options) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const pc = useRef<RTCPeerConnection | null>(null);
  const localStream = useRef<MediaStream | null>(null);
  const remoteAudio = useRef<HTMLAudioElement | null>(null);
  const statsTimer = useRef<number | undefined>(undefined);
  const disconnectTimer = useRef<number | undefined>(undefined);
  const lastBytes = useRef({ bytes: 0, at: 0 });
  const restartAttempts = useRef(0);
  const restartIceRef = useRef<() => Promise<void>>(async () => undefined);
  const [muted, setMuted] = useState(false);
  const [stats, setStats] = useState<NetworkStats>(EMPTY_STATS);

  const collectStats = useCallback(async () => {
    if (!pc.current) return;
    const report = await pc.current.getStats();
    let inbound: RTCInboundRtpStreamStats | undefined;
    let pair: RTCIceCandidatePairStats | undefined;
    let remoteCandidate: CandidateStats | undefined;
    report.forEach((item) => {
      if (item.type === "inbound-rtp" && item.kind === "audio") inbound = item;
      if (item.type === "candidate-pair" && item.state === "succeeded" && item.nominated) pair = item;
    });
    if (pair?.remoteCandidateId) {
      remoteCandidate = report.get(pair.remoteCandidateId) as CandidateStats | undefined;
    }
    if (!inbound) return;
    const now = performance.now();
    const elapsed = (now - lastBytes.current.at) / 1000;
    const bytesReceived = inbound.bytesReceived || 0;
    const packetsReceived = inbound.packetsReceived || 0;
    const packetsLost = inbound.packetsLost || 0;
    const bitrate =
      elapsed > 0 && lastBytes.current.at
        ? Math.max(0, ((bytesReceived - lastBytes.current.bytes) * 8) / elapsed / 1000)
        : 0;
    lastBytes.current = { bytes: bytesReceived, at: now };
    const total = packetsReceived + packetsLost;
    const packetLoss = total > 0 ? (packetsLost / total) * 100 : 0;
    const rtt = (pair?.currentRoundTripTime || 0) * 1000;
    const jitter = (inbound.jitter || 0) * 1000;
    const quality =
      packetLoss > 15 || rtt > 600 ? "poor" : packetLoss > 5 || rtt > 300 ? "fair" : "good";
    setStats({
      rtt,
      jitter,
      packetLoss,
      bitrate,
      candidate: remoteCandidate ? `${remoteCandidate.candidateType} / ${remoteCandidate.protocol}` : "—",
      mode: remoteCandidate?.candidateType === "relay" ? "relay" : remoteCandidate ? "direct" : "unknown",
      quality,
    });
  }, []);

  const tuneSender = useCallback(async (connection: RTCPeerConnection) => {
    const sender = connection.getSenders().find((item) => item.track?.kind === "audio");
    if (!sender) return;
    const parameters = sender.getParameters();
    parameters.encodings ||= [{}];
    parameters.encodings[0].maxBitrate = 24_000;
    parameters.encodings[0].networkPriority = "high";
    parameters.encodings[0].priority = "high";
    await sender.setParameters(parameters).catch(() => undefined);
  }, []);

  const ensurePeer = useCallback(async () => {
    if (pc.current) return pc.current;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,
      },
      video: false,
    });
    localStream.current = stream;
    const connection = new RTCPeerConnection({
      iceServers: optionsRef.current.iceServers,
      iceCandidatePoolSize: 4,
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
    });
    pc.current = connection;
    stream.getAudioTracks().forEach((track) => connection.addTrack(track, stream));
    await tuneSender(connection);
    connection.ontrack = (event) => {
      if (remoteAudio.current) {
        remoteAudio.current.srcObject = event.streams[0] || new MediaStream([event.track]);
        void remoteAudio.current.play().catch(() => undefined);
      }
    };
    connection.onicecandidate = (event) => {
      if (event.candidate && optionsRef.current.callId) {
        optionsRef.current.sendSignal({
          type: "ice-candidate",
          call_id: optionsRef.current.callId || undefined,
          payload: { candidate: event.candidate.toJSON() },
        });
      }
    };
    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "connected") {
        restartAttempts.current = 0;
        if (disconnectTimer.current) clearTimeout(disconnectTimer.current);
        optionsRef.current.onConnected();
      } else if (connection.connectionState === "disconnected") {
        optionsRef.current.onRecovering();
        disconnectTimer.current = window.setTimeout(() => void restartIceRef.current(), 3000);
      } else if (connection.connectionState === "failed") {
        void restartIceRef.current();
      }
    };
    statsTimer.current = window.setInterval(() => void collectStats(), 2000);
    return connection;
  }, [collectStats, tuneSender]);

  const makeOffer = useCallback(
    async (iceRestart = false) => {
      const connection = await ensurePeer();
      const offer = await connection.createOffer({ iceRestart });
      offer.sdp = preferOpusOnly(offer.sdp || "");
      await connection.setLocalDescription(offer);
      optionsRef.current.sendSignal({
        type: iceRestart ? "ice-restart" : "offer",
        call_id: optionsRef.current.callId || undefined,
        payload: { sdp: connection.localDescription },
      });
    },
    [ensurePeer],
  );

  const restartIce = useCallback(async () => {
    if (!pc.current || restartAttempts.current >= 5) {
      optionsRef.current.onFailed();
      return;
    }
    restartAttempts.current += 1;
    optionsRef.current.onRecovering();
    await makeOffer(true).catch(() => {
      window.setTimeout(
        () => void restartIceRef.current(),
        Math.min(15_000, 1000 * 2 ** restartAttempts.current),
      );
    });
  }, [makeOffer]);
  restartIceRef.current = restartIce;

  const handleSignal = useCallback(
    async (message: SignalEnvelope) => {
      const connection = await ensurePeer();
      if (message.type === "offer" || message.type === "ice-restart") {
        const description = message.payload?.sdp as RTCSessionDescriptionInit;
        await connection.setRemoteDescription(description);
        const answer = await connection.createAnswer();
        answer.sdp = preferOpusOnly(answer.sdp || "");
        await connection.setLocalDescription(answer);
        optionsRef.current.sendSignal({
          type: "answer",
          call_id: message.call_id,
          payload: { sdp: connection.localDescription },
        });
      } else if (message.type === "answer") {
        await connection.setRemoteDescription(message.payload?.sdp as RTCSessionDescriptionInit);
      } else if (message.type === "ice-candidate") {
        await connection.addIceCandidate(message.payload?.candidate as RTCIceCandidateInit).catch(() => undefined);
      }
    },
    [ensurePeer],
  );

  const cleanup = useCallback(() => {
    if (statsTimer.current) clearInterval(statsTimer.current);
    if (disconnectTimer.current) clearTimeout(disconnectTimer.current);
    pc.current?.close();
    pc.current = null;
    localStream.current?.getTracks().forEach((track) => track.stop());
    localStream.current = null;
    if (remoteAudio.current) remoteAudio.current.srcObject = null;
    setMuted(false);
    setStats(EMPTY_STATS);
  }, []);

  const toggleMute = useCallback(() => {
    const track = localStream.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setMuted(!track.enabled);
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  return {
    remoteAudio,
    muted,
    stats,
    ensurePeer,
    makeOffer,
    handleSignal,
    toggleMute,
    cleanup,
  };
}
